import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deleteCostCenter, getCostCenters, getPolicies } from '../../../../api/endpoints/corporate';
import type { CostCenter } from '../../../../api/types/corporate';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { CostCenterFormDialog } from '../CostCenterFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5). Ortak `ListPageScaffold` kullanır. */
export default function CostCenterListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CostCenter | null>(null);

  const query = useQuery({ queryKey: ['corporate', 'costCenters'], queryFn: () => getCostCenters() });
  // Politikalar form diyaloğundaki "Politika" select'ini besler — burada çekilip
  // create+edit diyalogları tek cache'i paylaşsın diye.
  const policiesQuery = useQuery({ queryKey: ['corporate', 'policies'], queryFn: () => getPolicies() });

  const rows: CostCenter[] = query.data?.costCenter ?? [];
  const policies = policiesQuery.data?.policies ?? [];

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteCostCenter(id),
    invalidateKeys: [['corporate', 'costCenters']],
    successMessage: 'Masraf merkezi silindi.',
  });
  const deletingName = rows.find((r) => r.costCenterID === del.target)?.costCenterName ?? '';

  const columns: DataTableColumn<CostCenter>[] = [
    { id: 'name', header: 'Masraf Merkezi Adı', cell: (r) => r.costCenterName },
    { id: 'policy', header: 'Politika', cell: (r) => r.policyName ?? '—' },
  ];

  return (
    <ListPageScaffold
      title="Masraf Merkezleri"
      createLabel="Yeni Masraf Merkezi"
      onCreate={() => setCreateOpen(true)}
      query={query}
      rows={rows}
      columns={columns}
      getRowKey={(r) => r.costCenterID}
      emptyTitle="Kayıtlı masraf merkezi bulunamadı."
      rowActions={(r) => (
        <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.costCenterID)} />
      )}
    >
      <CostCenterFormDialog open={createOpen} costCenter={null} policies={policies} onClose={() => setCreateOpen(false)} />
      <CostCenterFormDialog open={!!editing} costCenter={editing} policies={policies} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Masraf merkezini sil"
        description={`"${deletingName}" masraf merkezini silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
